import { useState, useCallback, useEffect } from 'react';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PipelineConfig, PipelineStage, LegacyStatusMapping } from '@recruitai/shared';
import { PIPELINE_LIMITS } from '@recruitai/shared';
import { pipelineApi } from '@/lib/pipeline-api';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { GripVertical, Plus, Trash2, Save, ChevronDown, ChevronUp, Mail } from 'lucide-react';
import { StageOwnerSelector } from './StageOwnerSelector';
import { TemplateSelector } from './TemplateSelector';
import { toast } from 'sonner';

interface OrgUser {
  userId: string;
  name: string;
  email: string;
  role: string;
}

interface PipelineConfiguratorProps {
  jobId?: string; // undefined when creating a new job
  initialConfig?: PipelineConfig;
  onConfigChange?: (config: PipelineConfig) => void;
}

const SYSTEM_STAGES_START: PipelineStage[] = [
  { stageId: 'sys-pending', name: 'Pendente', type: 'system', position: 0, legacyStatus: 'pending', owners: [] },
  { stageId: 'sys-analyzing', name: 'Analisando', type: 'system', position: 1, legacyStatus: 'analyzing', owners: [] },
];

const SYSTEM_STAGE_END: PipelineStage = {
  stageId: 'sys-hired', name: 'Contratado', type: 'system', position: 999, legacyStatus: 'hired', owners: [],
};

const LEGACY_STATUS_OPTIONS: { value: LegacyStatusMapping; label: string }[] = [
  { value: 'approved', label: 'Aprovado' },
  { value: 'interviewed', label: 'Entrevistado' },
  { value: 'pending', label: 'Pendente' },
  { value: 'analyzing', label: 'Analisando' },
  { value: 'hired', label: 'Contratado' },
  { value: 'rejected', label: 'Rejeitado' },
];

function SortableStageItem({ stage, onUpdate, onRemove, orgUsers, expanded, onToggle }: {
  stage: PipelineStage;
  onUpdate: (stage: PipelineStage) => void;
  onRemove: () => void;
  orgUsers: OrgUser[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: stage.stageId });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} className="border rounded-md p-3 bg-background space-y-2">
      <div className="flex items-center gap-2">
        <button type="button" {...attributes} {...listeners} className="cursor-grab" aria-label="Arrastar etapa">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
        <Input
          value={stage.name}
          onChange={e => onUpdate({ ...stage, name: e.target.value })}
          placeholder="Nome da etapa"
          className="h-8 text-sm flex-1"
          maxLength={PIPELINE_LIMITS.MAX_STAGE_NAME_LENGTH}
        />
        <select
          value={stage.legacyStatus}
          onChange={e => onUpdate({ ...stage, legacyStatus: e.target.value as LegacyStatusMapping })}
          className="h-8 text-xs border rounded px-2"
          aria-label="Status legado"
        >
          {LEGACY_STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <Button type="button" variant="ghost" size="sm" onClick={onToggle}>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onRemove} aria-label="Remover etapa">
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>

      {expanded && (
        <div className="pl-6 space-y-3">
          <div>
            <Label className="text-xs">Donos da Etapa</Label>
            <StageOwnerSelector
              owners={stage.owners}
              orgUsers={orgUsers}
              onChange={owners => onUpdate({ ...stage, owners })}
            />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="h-3 w-3 text-muted-foreground" />
              <Label className="text-xs">Template de Email (opcional)</Label>
            </div>
            <Input
              value={stage.emailTemplate?.subject || ''}
              onChange={e => onUpdate({
                ...stage,
                emailTemplate: { subject: e.target.value, body: stage.emailTemplate?.body || '', autoSend: stage.emailTemplate?.autoSend || false },
              })}
              placeholder="Assunto (ex: Próxima etapa: {stageName})"
              className="h-8 text-sm mb-1"
            />
            <Textarea
              value={stage.emailTemplate?.body || ''}
              onChange={e => onUpdate({
                ...stage,
                emailTemplate: { subject: stage.emailTemplate?.subject || '', body: e.target.value, autoSend: stage.emailTemplate?.autoSend || false },
              })}
              placeholder="Corpo do email (placeholders: {candidateName}, {jobTitle}, {stageName}, {orgName})"
              rows={2}
              className="text-sm"
            />
            <label className="flex items-center gap-2 mt-1 text-xs">
              <input
                type="checkbox"
                checked={stage.emailTemplate?.autoSend || false}
                onChange={e => onUpdate({
                  ...stage,
                  emailTemplate: {
                    subject: stage.emailTemplate?.subject || '',
                    body: stage.emailTemplate?.body || '',
                    autoSend: e.target.checked,
                  },
                })}
              />
              Enviar automaticamente ao mover candidato para esta etapa
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

export function PipelineConfigurator({ initialConfig, onConfigChange }: PipelineConfiguratorProps) {
  const [customStages, setCustomStages] = useState<PipelineStage[]>(() => {
    if (initialConfig?.isCustom) {
      return initialConfig.stages.filter(s => s.type === 'custom');
    }
    return [{ stageId: crypto.randomUUID(), name: 'Triagem', type: 'custom', position: 2, legacyStatus: 'approved', owners: [] }];
  });
  const [expandedStage, setExpandedStage] = useState<string | null>(null);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    apiClient.get<OrgUser[]>('/users').then(setOrgUsers).catch(() => {});
  }, []);

  const buildFullConfig = useCallback((stages: PipelineStage[]): PipelineConfig => {
    const allStages: PipelineStage[] = [
      ...SYSTEM_STAGES_START,
      ...stages.map((s, i) => ({ ...s, position: i + 2 })),
      { ...SYSTEM_STAGE_END, position: stages.length + 2 },
    ];
    return { stages: allStages, isCustom: true, updatedAt: '', updatedBy: '' };
  }, []);

  useEffect(() => {
    onConfigChange?.(buildFullConfig(customStages));
  }, [customStages, buildFullConfig, onConfigChange]);

  const addStage = () => {
    if (customStages.length >= PIPELINE_LIMITS.MAX_CUSTOM_STAGES) {
      toast.error(`Máximo de ${PIPELINE_LIMITS.MAX_CUSTOM_STAGES} etapas customizadas`);
      return;
    }
    const newStage: PipelineStage = {
      stageId: crypto.randomUUID(),
      name: '',
      type: 'custom',
      position: customStages.length + 2,
      legacyStatus: 'approved',
      owners: [],
    };
    setCustomStages(prev => [...prev, newStage]);
    setExpandedStage(newStage.stageId);
  };

  const updateStage = (updated: PipelineStage) => {
    setCustomStages(prev => prev.map(s => s.stageId === updated.stageId ? updated : s));
  };

  const removeStage = (stageId: string) => {
    if (customStages.length <= PIPELINE_LIMITS.MIN_CUSTOM_STAGES) {
      toast.error('O pipeline precisa de pelo menos 1 etapa customizada');
      return;
    }
    setCustomStages(prev => prev.filter(s => s.stageId !== stageId));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCustomStages(prev => {
      const oldIndex = prev.findIndex(s => s.stageId === active.id);
      const newIndex = prev.findIndex(s => s.stageId === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleTemplateSelect = (stages: PipelineStage[]) => {
    const customOnly = stages.filter(s => s.type === 'custom').map(s => ({
      ...s,
      stageId: crypto.randomUUID(), // new IDs to avoid conflicts
      owners: [], // owners are per-job, not per-template
    }));
    if (customOnly.length > 0) {
      setCustomStages(customOnly);
      toast.success('Template aplicado');
    }
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim() || templateName.length < 3) {
      toast.error('Nome do template deve ter pelo menos 3 caracteres');
      return;
    }
    setSavingTemplate(true);
    try {
      const config = buildFullConfig(customStages);
      await pipelineApi.createTemplate(templateName, config.stages);
      toast.success('Template salvo');
      setTemplateName('');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao salvar template');
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Pipeline de Etapas</span>
          <TemplateSelector onSelect={handleTemplateSelect} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* System stages (read-only) */}
        {SYSTEM_STAGES_START.map(stage => (
          <div key={stage.stageId} className="border rounded-md p-3 bg-muted/50 flex items-center gap-2">
            <Badge variant="outline" className="text-xs">Sistema</Badge>
            <span className="text-sm font-medium">{stage.name}</span>
          </div>
        ))}

        {/* Custom stages (drag-and-drop) */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={customStages.map(s => s.stageId)} strategy={verticalListSortingStrategy}>
            {customStages.map(stage => (
              <SortableStageItem
                key={stage.stageId}
                stage={stage}
                onUpdate={updateStage}
                onRemove={() => removeStage(stage.stageId)}
                orgUsers={orgUsers}
                expanded={expandedStage === stage.stageId}
                onToggle={() => setExpandedStage(prev => prev === stage.stageId ? null : stage.stageId)}
              />
            ))}
          </SortableContext>
        </DndContext>

        <Button type="button" variant="outline" size="sm" onClick={addStage} className="w-full">
          <Plus className="h-4 w-4 mr-1" /> Adicionar Etapa
        </Button>

        {/* System stage end (read-only) */}
        <div className="border rounded-md p-3 bg-muted/50 flex items-center gap-2">
          <Badge variant="outline" className="text-xs">Sistema</Badge>
          <span className="text-sm font-medium">{SYSTEM_STAGE_END.name}</span>
        </div>

        {/* Save as template */}
        <div className="flex items-center gap-2 pt-2 border-t">
          <Input
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            placeholder="Nome do template"
            className="h-8 text-sm"
            maxLength={PIPELINE_LIMITS.MAX_TEMPLATE_NAME_LENGTH}
          />
          <Button type="button" variant="outline" size="sm" onClick={saveAsTemplate} disabled={savingTemplate}>
            <Save className="h-4 w-4 mr-1" /> Salvar Template
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
